import { AlertTriangle, Star, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import clsx from 'clsx';
import type { Reservation, Guest } from '../../hooks/useShiftSetupData';

interface GuestAlertsBannerProps {
  reservations: Reservation[];
  guests: Guest[];
  assignments: Array<{ sectionId?: string; employeeId: string }>;
}

interface AllergyCount {
  name: string;
  count: number;
  guestNames: string[];
}

export default function GuestAlertsBanner({
  reservations,
  guests,
  assignments,
}: GuestAlertsBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Count VIPs
  const vipReservations = reservations.filter((res) => {
    const guest = guests.find((g) => g.id === res.guestId);
    return guest?.isVIP;
  });

  // Count VIPs needing server assignments
  const vipsNeedingServers = vipReservations.filter((res) => {
    const guest = guests.find((g) => g.id === res.guestId);
    if (!guest?.preferences?.preferredServer) return false;
    // Check if their preferred server is assigned
    return !assignments.some((a) => a.employeeId === guest.preferences?.preferredServer);
  });

  // Aggregate allergies
  const allergyMap: Record<string, AllergyCount> = {};
  for (const res of reservations) {
    const guest = guests.find((g) => g.id === res.guestId);
    if (guest?.allergies) {
      for (const allergy of guest.allergies) {
        const key = allergy.toLowerCase();
        if (!allergyMap[key]) {
          allergyMap[key] = { name: allergy, count: 0, guestNames: [] };
        }
        allergyMap[key].count++;
        allergyMap[key].guestNames.push(`${guest.firstName} ${guest.lastName}`);
      }
    }
  }
  const allergies = Object.values(allergyMap).sort((a, b) => b.count - a.count);
  const totalAllergyGuests = new Set(
    reservations
      .filter((res) => {
        const guest = guests.find((g) => g.id === res.guestId);
        return (guest?.allergies?.length || 0) > 0;
      })
      .map((res) => res.guestId)
  ).size;

  // Aggregate dietary restrictions
  const dietaryMap: Record<string, number> = {};
  for (const res of reservations) {
    const guest = guests.find((g) => g.id === res.guestId);
    if (guest?.dietaryRestrictions) {
      for (const restriction of guest.dietaryRestrictions) {
        const key = restriction.toLowerCase();
        dietaryMap[key] = (dietaryMap[key] || 0) + 1;
      }
    }
  }
  const dietaryRestrictions = Object.entries(dietaryMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);

  // No alerts to show
  if (vipReservations.length === 0 && allergies.length === 0 && dietaryRestrictions.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-50 to-purple-50 hover:from-amber-100 hover:to-purple-100 transition-colors"
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wide">
          <AlertTriangle className="w-4 h-4 text-amber-500" />
          Guest Alerts
        </h3>
        <div className="flex items-center gap-3">
          {vipReservations.length > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded-full">
              <Star className="w-3 h-3" />
              {vipReservations.length} VIP{vipReservations.length !== 1 ? 's' : ''}
            </span>
          )}
          {totalAllergyGuests > 0 && (
            <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
              <AlertTriangle className="w-3 h-3" />
              {totalAllergyGuests} allergy alert{totalAllergyGuests !== 1 ? 's' : ''}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 space-y-4">
          {/* VIP Alerts */}
          {vipReservations.length > 0 && (
            <div>
              <h4 className="flex items-center gap-2 text-xs font-medium text-purple-700 uppercase mb-2">
                <Star className="w-3 h-3" />
                VIP Guests ({vipReservations.length})
              </h4>
              <div className="space-y-2">
                {vipReservations.map((res) => {
                  const guest = guests.find((g) => g.id === res.guestId);
                  const needsServer =
                    guest?.preferences?.preferredServer &&
                    !assignments.some(
                      (a) => a.employeeId === guest.preferences?.preferredServer
                    );

                  return (
                    <div
                      key={res.id}
                      className={clsx(
                        'flex items-center justify-between p-2 rounded-lg',
                        needsServer ? 'bg-red-50 border border-red-200' : 'bg-purple-50'
                      )}
                    >
                      <div>
                        <span className="text-sm font-medium text-slate-900">
                          {res.guestName}
                        </span>
                        <span className="text-xs text-slate-500 ml-2">
                          Party of {res.partySize} at {res.time}
                        </span>
                      </div>
                      {needsServer && (
                        <span className="text-xs text-red-600 font-medium">
                          Preferred server not assigned
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Allergy Alerts */}
          {allergies.length > 0 && (
            <div>
              <h4 className="flex items-center gap-2 text-xs font-medium text-amber-700 uppercase mb-2">
                <AlertTriangle className="w-3 h-3" />
                Allergies
              </h4>
              <div className="flex flex-wrap gap-2">
                {allergies.map((allergy) => (
                  <div
                    key={allergy.name}
                    className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg"
                    title={`Guests: ${allergy.guestNames.join(', ')}`}
                  >
                    <span className="text-sm font-medium text-amber-800 capitalize">
                      {allergy.name}
                    </span>
                    <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                      {allergy.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dietary Restrictions */}
          {dietaryRestrictions.length > 0 && (
            <div>
              <h4 className="flex items-center gap-2 text-xs font-medium text-slate-600 uppercase mb-2">
                <Users className="w-3 h-3" />
                Dietary Restrictions
              </h4>
              <div className="flex flex-wrap gap-2">
                {dietaryRestrictions.map((restriction) => (
                  <div
                    key={restriction.name}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg"
                  >
                    <span className="text-sm font-medium text-slate-700 capitalize">
                      {restriction.name}
                    </span>
                    <span className="text-xs text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                      {restriction.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
